import React from 'react';
import { Check, Calendar, DollarSign, Sparkles } from 'lucide-react';

const PackageSelector = ({ packages, selectedPackage, onSelectPackage, roomPrice }) => {
  if (!packages || packages.length === 0) return null;

  const activePackages = packages.filter(pkg => pkg.enabled);
  if (activePackages.length === 0) return null;

  return (
    <div className="space-y-4">
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-purple-600 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-900 mb-1">
              📦 Long-Term Package Deals Available!
            </h4>
            <p className="text-sm text-gray-700">
              Save money with our monthly packages - perfect for students & medical professionals
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {activePackages.map((pkg) => {
          const isSelected = selectedPackage?.duration_months === pkg.duration_months;
          const savings = roomPrice && pkg.monthly_payment 
            ? Math.round(roomPrice - pkg.monthly_payment) 
            : 0;
          const savingsPercent = roomPrice && savings > 0
            ? Math.round((savings / roomPrice) * 100)
            : 0;

          return (
            <button
              key={pkg.duration_months}
              onClick={() => onSelectPackage(pkg)}
              className={`relative text-left p-4 rounded-lg border-2 transition-all ${
                isSelected
                  ? 'border-green-500 bg-green-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow'
              }`}
            >
              {/* Selected Checkmark */}
              {isSelected && (
                <div className="absolute top-3 right-3 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
              )}

              {/* Savings Badge */}
              {savingsPercent > 0 && (
                <div className="absolute top-0 left-4 -translate-y-1/2">
                  <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                    Save {savingsPercent}%
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {/* Duration */}
                <div className="flex items-center gap-2">
                  <Calendar className={`w-5 h-5 ${isSelected ? 'text-green-600' : 'text-gray-600'}`} />
                  <h3 className={`text-lg font-bold ${isSelected ? 'text-green-900' : 'text-gray-900'}`}>
                    {pkg.duration_months} Month Package
                  </h3>
                </div>

                {/* Pricing */}
                <div className="space-y-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      ${pkg.total_price.toLocaleString()}
                    </span>
                    <span className="text-sm text-gray-500">total</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-700">
                      ${pkg.monthly_payment}/month × {pkg.duration_months} payments
                    </span>
                  </div>
                  {savings > 0 && (
                    <p className="text-xs text-green-600">
                      💰 Save ${savings}/month vs regular price
                    </p>
                  )}
                </div>

                {/* Description */}
                {pkg.description && (
                  <p className="text-xs text-gray-600 italic mt-2">
                    {pkg.description}
                  </p>
                )}

                {/* Included Features */}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Included:</p>
                  <div className="grid grid-cols-2 gap-1 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-600" />
                      <span>All utilities</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-600" />
                      <span>Weekly cleaning</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-600" />
                      <span>Monthly payments</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Check className="w-3 h-3 text-green-600" />
                      <span>Flexible move-in</span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Clear Selection */}
      {selectedPackage && (
        <button
          onClick={() => onSelectPackage(null)}
          className="w-full text-sm text-gray-600 hover:text-gray-900 underline"
        >
          Clear package selection (book regular stay instead)
        </button>
      )}
    </div>
  );
};

export default PackageSelector;
